import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
} from "@angular/core";

import EasyMDE from "easymde";

@Component({
  standalone: false,
  selector: "app-markdown-editor",
  templateUrl: "./markdown-editor.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ["./markdown-editor.component.css"],
})
export class MarkdownEditorComponent
  implements OnChanges, AfterViewInit, OnDestroy
{
  private simpleMde?: EasyMDE;
  private suppressValueChange = false;

  @ViewChild("markdownEditorContainer")
  public markdownEditorContainer!: ElementRef<HTMLTextAreaElement>;

  @Input()
  public placeholder = "";

  @Input()
  public value = "";

  @Input()
  public spellcheck = false;

  @Output()
  public valueChange = new EventEmitter<string>();

  public get accessibleLabel(): string {
    const trimmedPlaceholder = (this.placeholder ?? "").trim();
    return trimmedPlaceholder || "Markdown editor";
  }

  public ngAfterViewInit(): void {
    this.simpleMde = this.getSimpleMde(
      this.markdownEditorContainer.nativeElement,
    );

    this.applyAccessibilityAttributes();
    this.applyEditorValue(this.value ?? "");
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes["placeholder"]) {
      this.applyAccessibilityAttributes();
    }

    const nextValue = changes["value"]?.currentValue;

    if (typeof nextValue !== "string") {
      return;
    }

    this.applyEditorValue(nextValue);
  }

  public ngOnDestroy(): void {
    if (!this.simpleMde) {
      return;
    }

    this.simpleMde.codemirror.off("change", this.handleEditorChange);
    this.simpleMde.toTextArea();
    this.simpleMde = undefined;
  }

  private applyEditorValue(nextValue: string): void {
    if (!this.simpleMde) {
      return;
    }

    const normalizedValue = nextValue ?? "";

    if (this.simpleMde.value() === normalizedValue) {
      return;
    }

    this.suppressValueChange = true;

    try {
      this.simpleMde.value(normalizedValue);
    } finally {
      this.suppressValueChange = false;
    }
  }

  private readonly handleEditorChange = () => {
    if (!this.simpleMde || this.suppressValueChange) {
      return;
    }

    this.valueChange.emit(this.simpleMde.value());
  };

  private getSimpleMde(element: HTMLTextAreaElement): EasyMDE {
    const simpleMde = new EasyMDE({
      autofocus: false,
      element,
      hideIcons: [
        "image",
        "fullscreen",
        "guide",
        "side-by-side",
        "quote",
        "heading",
      ],
      spellChecker: this.spellcheck,
      status: false,
      placeholder: this.placeholder,
    });

    simpleMde.codemirror.on("change", this.handleEditorChange);

    return simpleMde;
  }

  private applyAccessibilityAttributes(): void {
    const textarea = this.markdownEditorContainer?.nativeElement;
    if (!textarea) return;

    const label = this.accessibleLabel;

    textarea.setAttribute("aria-label", label);
    textarea.setAttribute("title", label);
    textarea.setAttribute(
      "placeholder",
      this.placeholder?.trim() ? this.placeholder : "Markdown editor",
    );
  }
}
